import { createUseStyles } from 'react-jss';
import { isIOS } from 'react-device-detect';
import {
  convertEasingToCSS,
  convertRadiusToCSS,
  convertSpacingsToCSS,
  convertThemeToCSS,
  convertTimingsToCSS,
  convertTypographyToCSS,
  convertShadowsToCSS,
  EasingValues,
  RadiusValues,
  SpacingsValues,
  tabFocusStyles,
  ThemeLightValues,
  ShadowLightValues,
  TimingValues,
  TypographyValues,
} from '@picsart/cascade';

import {
  deepMergeStyles,
  mediaQuery,
  mozScrollbars,
  ScrollbarCoverage,
  scrollbars,
  selection,
} from './helpers';
import aimStyles from './aim';
import resets, { pristineLinks } from './resets';
import { defaultFonts } from './fonts';
import { ITheme } from './theme';
import themeVariables from './colors';

const useBaseStyles = createUseStyles(
  ({ typography: { baseFontSize }, breakpoints, colors }: ITheme) => ({
    ...deepMergeStyles(resets, pristineLinks(), selection(), themeVariables, aimStyles, {
      '@global': {
        ':root': {
          '--max-width-01': '1096px',
          '--max-width-02': '1440px',
        },
        html: {
          height: isIOS ? '-webkit-fill-available' : 'auto',

          fontFamily: defaultFonts,
          fontSize: baseFontSize,
          body: {
            fontSize: baseFontSize,
          },

          '&.desktop': {
            minWidth: 1024,
            ...scrollbars({ coverage: ScrollbarCoverage.Global }),
            ...mozScrollbars(),
          },

          // cascade variables
          ...tabFocusStyles,
          ...convertSpacingsToCSS(SpacingsValues),
          ...convertTimingsToCSS(TimingValues),
          ...convertRadiusToCSS(RadiusValues),
          ...convertEasingToCSS(EasingValues),
          ...convertTypographyToCSS(TypographyValues),
          ...convertShadowsToCSS(ShadowLightValues),
          ...convertThemeToCSS(ThemeLightValues),
        },

        '.color--primary': {
          color: colors.primary,
        },

        '.color--secondary': {
          color: colors.secondary,
        },

        '.color--tertiary': {
          color: colors.tertiary,
        },

        '.color--success': {
          color: colors.success,
        },

        '.color--warning': {
          color: colors.warning,
        },

        '.color--error': {
          color: colors.error,
        },

        '.color--black': {
          color: colors.black,
        },

        '.color--white': {
          color: colors.white,
        },

        // Google reCAPTCH badge
        '.grecaptcha-badge': {
          bottom: '90px !important',
        },

        '& ins[data-ad-status="unfilled"]': {
          display: 'none !important',
        },

        // flex align
        '.flex': {
          display: 'flex',
        },
        '.align-center': {
          alignItems: 'center',
        },
        '.justify-start': {
          justifyContent: 'start',
        },
        '.justify-center': {
          justifyContent: 'center',
        },
        '.justify-end': {
          justifyContent: 'flex-end',
        },
        '.justify-space-between': {
          justifyContent: 'space-between',
        },
        '.fs-14': {
          fontSize: 14,
          fontWeight: 500,
          lineHeight: '20px',
        },
        '.fs-16': {
          fontSize: 16,
          fontWeight: 500,
          lineHeight: '24px',
        },
        '.fs-42': {
          fontSize: 42,
          fontWeight: 500,
          lineHeight: '50px',
        },
        '.internal-link': {
          color: 'var(--x-accent-2-1)',
        },

        // banner spacing
        '.landing-banner + .landing-banner': {
          marginTop: '80px',
        },

        [mediaQuery(breakpoints.tablet)]: {
          '.landing-banner + .landing-banner': {
            marginTop: '40px',
          },
        },
      },
    }),
  }),
);

export default useBaseStyles;
